
import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Notification, MissingEntriesPayload } from '../types';

interface NotificationPayload {
  message: string;
  type: 'missing_entries';
  payload: MissingEntriesPayload;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: NotificationPayload) => void;
  markAsRead: (notificationId: string) => void;
  clearNotifications: () => void;
  isModalOpen: boolean;
  modalContent: MissingEntriesPayload;
  showMissingEntriesModal: (content: MissingEntriesPayload) => void;
  hideMissingEntriesModal: () => void;
  isSoundEnabled: boolean;
  toggleSound: () => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
}

// Web Audio API setup for the beep sound
let audioContext: AudioContext;
const playBeep = () => {
    if (typeof window === 'undefined') return;

    if (!audioContext) {
        // Check for Safari and other browsers that require user interaction to start AudioContext
        if (window.AudioContext || (window as any).webkitAudioContext) {
             audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        } else {
            console.warn("Web Audio API is not supported in this browser.");
            return;
        }
    }

    // Resume context if it was suspended (e.g., autoplay policies)
    if (audioContext.state === 'suspended') {
        audioContext.resume();
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 note
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Adjust volume

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1); // Beep for 0.1 seconds
};


export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<MissingEntriesPayload>({ items: [] });
    
    const getInitialSoundEnabled = (): boolean => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                const saved = localStorage.getItem('lam-custos-notification-sound');
                if (saved !== null) {
                    return JSON.parse(saved);
                }
            }
        } catch (error) {
            console.error('Failed to parse sound setting from localStorage, resetting to default.', error);
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem('lam-custos-notification-sound');
            }
        }
        return true; // Default to true if anything goes wrong
    };

    const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(getInitialSoundEnabled);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('lam-custos-notification-sound', JSON.stringify(isSoundEnabled));
        }
    }, [isSoundEnabled]);

    const toggleSound = () => {
        setIsSoundEnabled(prev => !prev);
    };

    const addNotification = useCallback((data: NotificationPayload) => {
        const newNotification: Notification = {
            id: `notif_${Date.now()}`,
            read: false,
            createdAt: new Date().toISOString(),
            ...data,
        };
        // Avoid adding duplicate notifications for the same report check
        const isDuplicate = notifications.some(
          n => n.message === newNotification.message && JSON.stringify(n.payload) === JSON.stringify(newNotification.payload)
        );
        if (!isDuplicate) {
          setNotifications(current => [newNotification, ...current]);
          if (isSoundEnabled) {
              playBeep();
          }
        }
    }, [notifications, isSoundEnabled]);

    const markAsRead = useCallback((notificationId: string) => {
        setNotifications(current => 
            current.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
    }, []);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const showMissingEntriesModal = useCallback((content: MissingEntriesPayload) => {
        setModalContent(content);
        setIsModalOpen(true);
    }, []);

    const hideMissingEntriesModal = useCallback(() => {
        setIsModalOpen(false);
        setModalContent({ items: [] });
    }, []);
    
    return (
        <NotificationContext.Provider value={{ notifications, addNotification, markAsRead, clearNotifications, isModalOpen, modalContent, showMissingEntriesModal, hideMissingEntriesModal, isSoundEnabled, toggleSound }}>
            {children}
        </NotificationContext.Provider>
    );
};